import { Appointment, AppointmentStatus, deriveWorkState } from "@myapp/contract";
import { ISelectOption } from "../../../shared/types/ISelectOption";

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  PLANNED: "Geplant",
  FOLLOW_UP: "Folgetermin geplant",
  COMPLETED: "Abgeschlossen",
  COMPLETED_WITH_FOLLOW_UP: "Folgetermin erforderlich",
  EXPIRED: "Abgelaufen",
};

const IN_PROGRESS_COLOR = "#ED9E42";

type AppointmentLike = Pick<Appointment, 'status' | 'schedule'>;

/**
 * Something is happening on this Termin right now. One rule for the whole app —
 * `deriveWorkState` in the contract — so the Termine table, the board, the worker calendar and the
 * server's own in-progress queries cannot disagree about it.
 */
export const isAppointmentRunning = (a: AppointmentLike): boolean =>
  deriveWorkState(a).isRunning;

/**
 * The terminal states: COMPLETED, COMPLETED_WITH_FOLLOW_UP ("the visit happened and its actuals are
 * written; it only differs in owing a successor Termin"), and EXPIRED. Same set the contract uses
 * to decide that an open phase is a leftover rather than a session.
 */
export const isAppointmentClosed = (status: AppointmentStatus): boolean =>
  deriveWorkState({ status, schedule: null }).isClosed;

/**
 * Pass the appointment, not just its status, wherever you have one: without it the label can only
 * ever say what the status says, and a running Termin reads as „Geplant“.
 */
export const getAppointmentStatusLabel = (
  status: AppointmentStatus,
  appointment?: AppointmentLike,
): string => {
  if (appointment) {
    const { label } = deriveWorkState(appointment);
    if (label) return label;
  }
  return STATUS_LABELS[status];
};

const STATUS_COLORS: Record<AppointmentStatus, string> = {
  PLANNED: "#2EB5D9",
  FOLLOW_UP: "#2EB5D9",
  COMPLETED: "#3BC67D",
  COMPLETED_WITH_FOLLOW_UP: "#ED9E42",
  EXPIRED: "#9CA3AF",
};

export const getAppointmentStatusColor = (
  status: AppointmentStatus,
  appointment?: AppointmentLike,
): string => {
  // Every open phase reads as "in progress", not only ARBEIT. Without this, "Anfahrt läuft" painted
  // the same blue as "Geplant" and "Abfahrt läuft" on an already-completed appointment painted
  // green — an active session did not look active.
  if (appointment && deriveWorkState(appointment).isRunning) return IN_PROGRESS_COLOR;
  return STATUS_COLORS[status];
};

/**
 * Every status, for the select in the edit form. All five are offered — including EXPIRED, which
 * the nightly cron normally owns — because the form exists to correct a status the system got
 * wrong, and a state a manager cannot leave is the bug it is there to fix.
 *
 * "In Bearbeitung" is deliberately absent: it is derived from the schedule's actual timestamps,
 * not a stored status, so there is nothing here to select.
 */
export const appointmentStatusOptions: ISelectOption[] = (
  Object.keys(STATUS_LABELS) as AppointmentStatus[]
).map((status) => ({ value: status, label: STATUS_LABELS[status] }));

/**
 * What a *new* Termin can start as: the two planning states, and nothing a visit has to earn.
 * "Folgetermin geplant" is how a manager books the next visit before the technician has asked for
 * it; the backend would otherwise only assign it once the Auftrag is waiting for one.
 */
export const createAppointmentStatusOptions: ISelectOption[] = (
  ["PLANNED", "FOLLOW_UP"] as const
).map((status) => ({ value: status, label: STATUS_LABELS[status] }));
